import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useCookies } from "react-cookie";
import {
  Box,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Avatar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Alert,
  Snackbar,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import Layout from "../components/layout/CompliceLayout";
import config from "../config.js";

const StakeholderManagement = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedStakeholder, setSelectedStakeholder] = useState(null);
  const [cookies] = useCookies(["userId"]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [stakeholders, setStakeholders] = useState([]);
  const { API_BASE_URL } = config;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: {
      name: "",
      role: "",
      organization: "",
      email: "",
      phone: "",
      type: "",
      influence: "",
      tags: "",
    },
  });

  useEffect(() => {
    if (cookies.userId) {
      fetchStakeholders();
    }
  }, [cookies.userId]);

  const fetchStakeholders = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/stakeholders/${cookies.userId}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch stakeholders');
      const data = await response.json();
      setStakeholders(data);
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Error fetching stakeholders",
        severity: "error",
      });
    }
  };

  const handleOpenDialog = (stakeholder = null) => {
    if (stakeholder) {
      Object.keys(stakeholder).forEach((key) => {
        if (key === "tags") {
          setValue(key, stakeholder[key].join(", "));
        } else {
          setValue(key, stakeholder[key]);
        }
      });
      setSelectedStakeholder(stakeholder);
    } else {
      reset();
      setSelectedStakeholder(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    reset();
    setSelectedStakeholder(null);
  };

  const onSubmit = async (data) => {
    try {
      const formattedData = {
        ...data,
        userId: cookies.userId, // Add userId to the request
        tags: data.tags
          .split(",")
          .map((tag) => tag.trim())
          .filter(Boolean),
      };

      const url = selectedStakeholder
        ? `${API_BASE_URL}/api/v1/stakeholders/${selectedStakeholder._id}`
        : `${API_BASE_URL}/api/v1/stakeholders`;

      const response = await fetch(url, {
        method: selectedStakeholder ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(formattedData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save stakeholder');
      }
      
      const result = await response.json();

      if (selectedStakeholder) {
        setStakeholders(
          stakeholders.map((s) =>
            s._id === selectedStakeholder._id ? result : s
          )
        );
      } else {
        setStakeholders([...stakeholders, result]);
      }

      setSnackbar({
        open: true,
        message: `Stakeholder successfully ${
          selectedStakeholder ? "updated" : "created"
        }`,
        severity: "success",
      });
      handleCloseDialog();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Error saving stakeholder",
        severity: "error",
      });
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/stakeholders/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to delete stakeholder');

      setStakeholders(stakeholders.filter((s) => s._id !== id));
      setSnackbar({
        open: true,
        message: "Stakeholder successfully deleted",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Error deleting stakeholder",
        severity: "error",
      });
    }
  };

  return (
    <Layout>
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Stakeholder Management
        </Typography>

        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ mb: 3 }}
        >
          Add New Stakeholder
        </Button>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Organization</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Influence</TableCell>
                <TableCell>Tags</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stakeholders.map((stakeholder) => (
                <TableRow key={stakeholder._id}>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Avatar>{stakeholder.name[0]}</Avatar>
                      {stakeholder.name}
                    </Box>
                  </TableCell>
                  <TableCell>{stakeholder.role}</TableCell>
                  <TableCell>{stakeholder.organization}</TableCell>
                  <TableCell>
                    <Chip
                      label={stakeholder.type}
                      color={
                        stakeholder.type === "Executive" ? "primary" : "default"
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={stakeholder.influence}
                      color={
                        stakeholder.influence === "High"
                          ? "error"
                          : stakeholder.influence === "Medium"
                          ? "warning"
                          : "success"
                      }
                    />
                  </TableCell>
                  <TableCell>
                    {stakeholder.tags.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        sx={{ mr: 0.5 }}
                      />
                    ))}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => handleOpenDialog(stakeholder)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDelete(stakeholder._id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogTitle>
              {selectedStakeholder ? "Edit Stakeholder" : "Add New Stakeholder"}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="name"
                    control={control}
                    rules={{ required: "Name is required" }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Name"
                        error={!!errors.name}
                        helperText={errors.name?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="role"
                    control={control}
                    rules={{ required: "Role is required" }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Role"
                        error={!!errors.role}
                        helperText={errors.role?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="organization"
                    control={control}
                    rules={{ required: "Organization is required" }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Organization"
                        error={!!errors.organization}
                        helperText={errors.organization?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="email"
                    control={control}
                    rules={{
                      required: "Email is required",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Invalid email address",
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Email"
                        error={!!errors.email}
                        helperText={errors.email?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field }) => (
                      <TextField {...field} fullWidth label="Phone" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="type"
                    control={control}
                    rules={{ required: "Type is required" }}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!errors.type}>
                        <InputLabel>Type</InputLabel>
                        <Select {...field} label="Type">
                          <MenuItem value="Executive">Executive</MenuItem>
                          <MenuItem value="Internal">Internal</MenuItem>
                          <MenuItem value="External">External</MenuItem>
                        </Select>
                        {errors.type && (
                          <FormHelperText>{errors.type.message}</FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="influence"
                    control={control}
                    rules={{ required: "Influence level is required" }}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!errors.influence}>
                        <InputLabel>Influence</InputLabel>
                        <Select {...field} label="Influence">
                          <MenuItem value="High">High</MenuItem>
                          <MenuItem value="Medium">Medium</MenuItem>
                          <MenuItem value="Low">Low</MenuItem>
                        </Select>
                        {errors.influence && (
                          <FormHelperText>
                            {errors.influence.message}
                          </FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="tags"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Tags (comma-separated)"
                        helperText="Enter tags separated by commas"
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button type="submit" variant="contained" color="primary">
                {selectedStakeholder ? "Update" : "Create"}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default StakeholderManagement;
